// Карта для родителей: цена на пине, кластеры при отдалении. Кластеры —
// простая сетка по экрану (без сторонней библиотеки): точки в одной ячейке
// сливаются в кружок с числом, нажатие приближает к ним.
// Скрипт Google Maps грузится только когда карта видна (лимит бесплатных
// загрузок): на телефоне — после нажатия «Карта». Карта на первом экране
// (компьютер) грузится сразу — без ожидания IntersectionObserver: тот
// срабатывает только на отрисованном кадре, и карта оставалась серой до прокрутки.
//
// Нажатие на метку (18.10.2026): на компьютере — окошко у метки (InfoWindow)
// с карточкой `popup`, на телефоне окошка нет — страница показывает нижнюю
// шторку с той же карточкой. Выделение карточки в списке — как раньше.
//
// Карта /tutors «как у Airbnb» (24.10.2026): gestures="greedy" — один палец
// двигает карту, щипок и двойной тап зумят, колесо зумит без Ctrl (мини-карты
// главной и профиля остаются как были: там карта внутри прокручиваемой
// страницы). hoverId/onHover — подсветка метки при наведении на карточку и
// наоборот. onBounds — видимая область после каждой остановки карты: список
// /tutors показывает анкеты в ней (26.10.2026, как у Airbnb; кнопка «Искать в этой области» убрана).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { loadMaps } from "./PinPicker";
import type { Me, Spot } from "./my-location";

/** featured — анкета в блоке «Рекомендуемые»: метка крупнее, с зелёной обводкой. */
export type MapPoint = { id: string; lat: number; lng: number; label: string; club: boolean; featured?: boolean };
/** Видимая область карты: юг, запад, север, восток. */
export type MapArea = { s: number; w: number; n: number; e: number };

const BAKU = { lat: 40.4093, lng: 49.8671 };
/** Вид по умолчанию: весь Баку (тот же — у статичной подложки главной). */
const DEFAULT_VIEW = { center: BAKU, zoom: 12 };
/** Узкий экран — та же граница, что у раскладки «Список / Карта» в tutors-map.css. */
export const NARROW_QUERY = "(max-width: 880px)";
/**
 * Подложка без точек интереса: подписи и значки POI (больницы, мечети,
 * магазины, бизнес) скрыты, заливка парков и транспорт (метро) остаются.
 * mapId не задан — styles работают. PinPicker (форма репетитора) НЕ трогаем:
 * там POI помогают найти свой дом. Тот же стиль — у статичной подложки
 * главной (static-map.ts).
 */
export const MAP_STYLES = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
];
/** Размер ячейки кластера на экране, px. */
const CELL_PX = 56;
/** Цвета меток: выбранная — янтарная, и ничего другого янтарного на карте нет. */
export const PIN_COLORS = { tutor: "#1E5A6B", club: "#143F4B", active: "#F0A63A", home: "#7A3FC4", clusterRing: "#1E5A6B", hover: "#0F2A38", featured: "#4BAE8C" } as const;

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;");

/* eslint-disable @typescript-eslint/no-explicit-any */
function pillIcon(google: any, text: string, active: boolean, club: boolean, hover = false, featured = false) {
  const w = Math.max(40, 14 + text.length * 7.4);
  // Наведение — тёмная метка (не янтарная: янтарь только у выбранной).
  const bg = active ? PIN_COLORS.active : hover ? PIN_COLORS.hover : club ? PIN_COLORS.club : PIN_COLORS.tutor;
  const fg = active ? "#0F2A38" : "#FFFFFF";
  // Рекомендуемая (02.11.2026) — зелёная обводка бренда; размер — scale ниже.
  const ring = featured ? `stroke="${PIN_COLORS.featured}" stroke-width="2"` : `stroke="#0F2A38" stroke-opacity=".25"`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="34"><rect x="1" y="1" width="${w - 2}" height="26" rx="13" fill="${bg}" ${ring}/><path d="M${w / 2 - 5} 26 L${w / 2} 33 L${w / 2 + 5} 26 Z" fill="${bg}"/><text x="${w / 2}" y="18.5" text-anchor="middle" font-family="Nunito Sans,Segoe UI,sans-serif" font-size="13" font-weight="900" fill="${fg}">${esc(text)}</text></svg>`;
  const k = featured ? 1.15 : 1;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(w * k, 34 * k), anchor: new google.maps.Point((w * k) / 2, 34 * k) };
}

/** Кластер — белый кружок с бирюзовой обводкой: не путать с янтарной выбранной меткой. */
function clusterIcon(google: any, n: number, hover = false) {
  const d = n < 10 ? 38 : 46;
  // Наведённая анкета внутри кластера — кружок заливается тёмным.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${d}" height="${d}"><circle cx="${d / 2}" cy="${d / 2}" r="${d / 2 - 2}" fill="${hover ? PIN_COLORS.hover : "#FFFFFF"}" stroke="${PIN_COLORS.clusterRing}" stroke-width="3"/><text x="${d / 2}" y="${d / 2 + 5}" text-anchor="middle" font-family="Nunito Sans,Segoe UI,sans-serif" font-size="15" font-weight="900" fill="${hover ? "#FFFFFF" : PIN_COLORS.clusterRing}">${n}</text></svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(d, d), anchor: new google.maps.Point(d / 2, d / 2) };
}

/** Метка «Evim» — свой цвет и домик, подпись справа. */
function homeIcon(google: any, text: string) {
  const w = Math.max(64, 40 + text.length * 7.4);
  const c = PIN_COLORS.home;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="38"><rect x="1" y="1" width="${w - 2}" height="30" rx="15" fill="${c}" stroke="#FFFFFF" stroke-width="2"/><path d="M${w / 2 - 6} 30 L${w / 2} 37 L${w / 2 + 6} 30 Z" fill="${c}"/><path d="M11 16.5 L19 9.5 L27 16.5 M13.5 14.5 V23 H24.5 V14.5" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linejoin="round" stroke-linecap="round"/><text x="32" y="21" font-family="Nunito Sans,Segoe UI,sans-serif" font-size="13" font-weight="900" fill="#FFFFFF">${esc(text)}</text></svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(w, 38), anchor: new google.maps.Point(w / 2, 38) };
}

/** Карта уже на экране (или рядом с ним) — грузить сразу, не ждать IntersectionObserver. */
function onScreen(node: HTMLElement, margin = 200): boolean {
  const r = node.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false; // скрыта (телефон, «Список»)
  return r.top < window.innerHeight + margin && r.bottom > -margin;
}

export function MapCanvas({
  mapsKey,
  lang,
  points,
  activeId,
  onSelect,
  onClose,
  popup,
  labels,
  center,
  zoom,
  me,
  meLabel,
  pickMode,
  onPick,
  focus,
  gestures,
  hoverId,
  onHover,
  onBounds,
  area,
}: {
  mapsKey: string | null;
  lang: "az" | "ru" | "en";
  points: MapPoint[];
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Окошко закрыли (крестик, нажатие на пустую карту). */
  onClose?: () => void;
  /** Карточка выбранной метки — в окошке у метки на компьютере. На узком экране не показывается. */
  popup?: ReactNode;
  labels: { loading: string; error: string };
  /** Начальный центр и масштаб (мини-карта профиля); по умолчанию — Баку, 12. */
  center?: { lat: number; lng: number };
  zoom?: number;
  /** Родитель на карте: геоточка с кругом точности или перетаскиваемая метка «Evim». */
  me?: Me | null;
  /** Подпись точки и метки: «Siz buradasınız», «Evim». */
  meLabel?: { gps: string; home: string };
  /** Режим «Xəritədə qeyd et»: нажатие на карту ставит метку. */
  pickMode?: boolean;
  /** Нажатие на карту в режиме метки и перетаскивание метки «Evim». */
  onPick?: (p: Spot) => void;
  /**
   * Куда навести карту; n меняется — карта едет снова (та же точка второй раз).
   * fit — ещё точки, которые должны попасть в кадр (ближайшие метки к «Evim»).
   */
  focus?: (Spot & { zoom: number; n: number; fit?: Spot[] }) | null;
  /** greedy — карта забирает все жесты (карта /tutors). Не задано — как решит Google. */
  gestures?: "greedy";
  /** Анкета под курсором в списке — её метка (или кластер) подсвечена. */
  hoverId?: string | null;
  /** Курсор на метке / ушёл с неё. */
  onHover?: (id: string | null) => void;
  /** Видимая область — после каждой остановки карты (сдвиг, зум, наведение кодом). */
  onBounds?: (a: MapArea) => void;
  /** Мини-карта профиля (28.10.2026): круг ~500 м вместо метки — без точного адреса. */
  area?: { lat: number; lng: number; radius: number } | null;
}) {
  const el = useRef<HTMLDivElement>(null);
  // byId — метка (или кластер) каждой анкеты: наведение меняет одну иконку, без перерисовки всех меток.
  const g = useRef<{ google?: any; map?: any; markers: any[]; info?: any; byId: Map<string, { m: any; p?: MapPoint; n?: number }> }>({ markers: [], byId: new Map() });
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<"loading" | "ready" | "failed">(mapsKey ? "loading" : "failed");
  const latest = useRef({ points, activeId, onSelect, onClose, pickMode, onPick, hoverId, onHover, onBounds });
  latest.current = { points, activeId, onSelect, onClose, pickMode, onPick, hoverId, onHover, onBounds };
  const meLayer = useRef<{ dot?: any; ring?: any; home?: any }>({});
  // Последнее наведение — для кадра при создании карты; n наведения, уже применённого при создании.
  const latestFocus = useRef(focus);
  latestFocus.current = focus;
  const appliedFocusN = useRef<number | null>(null);
  // Узел содержимого окошка: React рисует в него карточку порталом.
  const [popupNode] = useState(() => (typeof document === "undefined" ? null : document.createElement("div")));

  useEffect(() => {
    const node = el.current;
    if (!mapsKey || !node || visible) return;
    if (onScreen(node)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setVisible(true), { rootMargin: "200px" });
    io.observe(node);
    return () => io.disconnect();
  }, [mapsKey, visible]);

  function redraw() {
    const { google, map } = g.current;
    if (!google || !map) return;
    for (const m of g.current.markers) m.setMap(null);
    g.current.markers = [];
    g.current.byId = new Map();
    const hot = latest.current.hoverId;
    const proj = map.getProjection();
    const zoom = map.getZoom() ?? 12;
    if (!proj) return;
    const scale = 2 ** zoom;
    const cells = new Map<string, MapPoint[]>();
    for (const p of latest.current.points) {
      const w = proj.fromLatLngToPoint(new google.maps.LatLng(p.lat, p.lng));
      const key = `${Math.floor((w.x * scale) / CELL_PX)}:${Math.floor((w.y * scale) / CELL_PX)}`;
      const list = cells.get(key) ?? [];
      list.push(p);
      cells.set(key, list);
    }
    for (const list of cells.values()) {
      // Выбранная метка не прячется в кластер — у неё открыто окошко.
      const activeInside = list.some((p) => p.id === latest.current.activeId);
      if (list.length === 1 || zoom >= 17 || activeInside) {
        for (const p of list) {
          const active = p.id === latest.current.activeId;
          const m = new google.maps.Marker({ map, position: { lat: p.lat, lng: p.lng }, icon: pillIcon(google, p.label, active, p.club, p.id === hot, p.featured), title: p.label, zIndex: active ? 1000 : p.id === hot ? 900 : p.featured ? 20 : 10 });
          m.addListener("click", () => latest.current.onSelect(p.id));
          m.addListener("mouseover", () => latest.current.onHover?.(p.id));
          m.addListener("mouseout", () => latest.current.onHover?.(null));
          g.current.markers.push(m);
          g.current.byId.set(p.id, { m, p });
        }
      } else {
        const b = new google.maps.LatLngBounds();
        for (const p of list) b.extend({ lat: p.lat, lng: p.lng });
        const m = new google.maps.Marker({ map, position: b.getCenter(), icon: clusterIcon(google, list.length, list.some((p) => p.id === hot)), title: String(list.length), zIndex: 500 });
        m.addListener("click", () => map.fitBounds(b, 60));
        g.current.markers.push(m);
        for (const p of list) g.current.byId.set(p.id, { m, n: list.length });
      }
    }
  }

  useEffect(() => {
    if (!mapsKey || !visible || !el.current) return;
    let cancelled = false;
    loadMaps(mapsKey, lang)
      .then(async (google: any) => {
        if (cancelled || !el.current) return;
        // GMap, а не Map: иначе ниже `new Map()` (byId) создаёт карту Google вместо словаря.
        const { Map: GMap, InfoWindow } = await google.maps.importLibrary("maps");
        await google.maps.importLibrary("marker");
        // Наведение уже задано (кадр «Баку + Абшерон» на /tutors) — карта сразу в нём.
        // Иначе метки рисовались в кадре по умолчанию и уезжали при смене масштаба:
        // это сдвиг макета (CLS до 0,26 в Lighthouse, 02.11.2026).
        const f = latestFocus.current;
        const initial = f && !f.fit?.length ? f : null;
        if (initial) appliedFocusN.current = initial.n;
        const map = new GMap(el.current, {
          center: initial ? { lat: initial.lat, lng: initial.lng } : (center ?? DEFAULT_VIEW.center),
          zoom: initial ? initial.zoom : (zoom ?? DEFAULT_VIEW.zoom),
          styles: MAP_STYLES,
          clickableIcons: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          ...(gestures ? { gestureHandling: gestures } : {}),
        });
        // Окошко у метки: одно на карту, позиция — по точке (метки пересоздаются при каждой перерисовке).
        const info = popupNode ? new InfoWindow({ content: popupNode, pixelOffset: new google.maps.Size(0, -34), maxWidth: 320, headerDisabled: true }) : undefined;
        info?.addListener("closeclick", () => latest.current.onClose?.());
        g.current = { google, map, markers: [], info, byId: new Map() };
        if (area) new google.maps.Circle({ map, center: { lat: area.lat, lng: area.lng }, radius: area.radius, strokeColor: "#F0A63A", strokeOpacity: 0.9, strokeWeight: 2, fillColor: "#F0A63A", fillOpacity: 0.18, clickable: false });
        map.addListener("idle", () => {
          redraw();
          const b = map.getBounds();
          if (b && latest.current.onBounds) {
            const ne = b.getNorthEast();
            const sw = b.getSouthWest();
            latest.current.onBounds({ s: sw.lat(), w: sw.lng(), n: ne.lat(), e: ne.lng() });
          }
        });
        map.addListener("click", (e: any) => {
          // Метка «Evim»: в режиме метки нажатие на карту ставит её сюда.
          if (latest.current.pickMode) {
            if (e.latLng) latest.current.onPick?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            return;
          }
          // Нажатие на пустую карту закрывает окошко (и шторку на телефоне).
          if (latest.current.activeId) latest.current.onClose?.();
        });
        setState("ready");
      })
      .catch(() => !cancelled && setState("failed"));
    return () => {
      cancelled = true;
    };
    // Карта создаётся один раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsKey, lang, visible]);

  // Новые точки или выбранный пин — перерисовать; выбранный — в центр, окошко — к нему.
  useEffect(() => {
    if (state !== "ready") return;
    redraw();
    const p = points.find((x) => x.id === activeId);
    const { map, info } = g.current;
    if (p) map?.panTo({ lat: p.lat, lng: p.lng });
    const narrow = window.matchMedia(NARROW_QUERY).matches;
    if (p && info && popup && !narrow) {
      info.setPosition({ lat: p.lat, lng: p.lng });
      info.open({ map, shouldFocus: false });
    } else info?.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, activeId, state]);

  // Наведение на карточку в списке — сменить иконку одной метки (или её кластера), без перерисовки.
  const hoverPrev = useRef<string | null>(null);
  useEffect(() => {
    const { google, byId } = g.current;
    if (state !== "ready" || !google) return;
    const paint = (id: string | null, on: boolean) => {
      const x = id ? byId.get(id) : undefined;
      if (!x) return;
      if (x.p) {
        const active = x.p.id === latest.current.activeId;
        x.m.setIcon(pillIcon(google, x.p.label, active, x.p.club, on, x.p.featured));
        x.m.setZIndex(active ? 1000 : on ? 900 : x.p.featured ? 20 : 10);
      } else if (x.n) x.m.setIcon(clusterIcon(google, x.n, on));
    };
    paint(hoverPrev.current, false);
    paint(hoverId ?? null, true);
    hoverPrev.current = hoverId ?? null;
  }, [hoverId, state]);

  // Родитель на карте: синяя точка с кругом точности (геоточка) или
  // перетаскиваемая метка «Evim» (свой цвет и домик). Слой отдельный от пинов: redraw его не трогает.
  useEffect(() => {
    const { google, map } = g.current;
    if (state !== "ready" || !google || !map) return;
    const L = meLayer.current;
    for (const k of ["dot", "ring", "home"] as const) {
      L[k]?.setMap(null);
      L[k] = undefined;
    }
    if (!me) return;
    const pos = { lat: me.lat, lng: me.lng };
    if (me.kind === "gps") {
      if (me.accuracy) {
        L.ring = new google.maps.Circle({ map, center: pos, radius: me.accuracy, strokeColor: "#1A73E8", strokeOpacity: 0.35, strokeWeight: 1, fillColor: "#1A73E8", fillOpacity: 0.12, clickable: false });
      }
      L.dot = new google.maps.Marker({
        map,
        position: pos,
        title: meLabel?.gps,
        zIndex: 2000,
        icon: { path: google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#1A73E8", fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3 },
      });
    } else {
      L.home = new google.maps.Marker({ map, position: pos, title: meLabel?.home, zIndex: 2000, draggable: true, icon: homeIcon(google, meLabel?.home ?? "") });
      L.home.addListener("dragend", (e: any) => e.latLng && latest.current.onPick?.({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.lat, me?.lng, me?.kind, me?.accuracy, state]);

  // Навести карту на родителя (после геоточки, адреса, метки): родитель в
  // центре кадра, ближайшие метки (fit) — в кадре; без них — масштаб zoom.
  // Карта скрыта (телефон, адрес выбран в «Siyahı») — fitBounds по нулевому
  // размеру дал бы масштаб континента. Наведение ждёт, пока у карты появится размер.
  const pendingFocus = useRef<typeof focus>(null);
  function applyFocus(f: NonNullable<typeof focus>) {
    const { google, map } = g.current;
    if (!map) return;
    const node = el.current;
    if (!node || node.clientWidth === 0 || node.clientHeight === 0) {
      pendingFocus.current = f;
      return;
    }
    pendingFocus.current = null;
    if (f.fit?.length && google) {
      // Кадр симметричен относительно родителя: он остаётся в центре.
      const b = new google.maps.LatLngBounds();
      b.extend({ lat: f.lat, lng: f.lng });
      for (const p of f.fit) {
        b.extend(p);
        b.extend({ lat: 2 * f.lat - p.lat, lng: 2 * f.lng - p.lng });
      }
      map.fitBounds(b, 48);
      google.maps.event.addListenerOnce(map, "idle", () => {
        if ((map.getZoom() ?? 0) > 16) map.setZoom(16);
      });
      return;
    }
    map.setCenter({ lat: f.lat, lng: f.lng });
    map.setZoom(f.zoom);
  }
  useEffect(() => {
    // Это наведение карта получила при создании — второй раз не двигаем.
    if (state !== "ready" || !focus || focus.n === appliedFocusN.current) return;
    applyFocus(focus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.n, state]);
  // Карта снова видна — отложенное наведение.
  useEffect(() => {
    const node = el.current;
    if (state !== "ready" || !node || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (pendingFocus.current && node.clientWidth > 0 && node.clientHeight > 0) applyFocus(pendingFocus.current);
    });
    ro.observe(node);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // В режиме метки — курсор-прицел.
  useEffect(() => {
    g.current.map?.setOptions({ draggableCursor: pickMode ? "crosshair" : null });
  }, [pickMode, state]);

  return (
    <div className="tm-map">
      <div ref={el} className="tm-map__canvas" role="region" aria-label="Map" />
      {state === "loading" && <span className="tm-map__note">{labels.loading}</span>}
      {state === "failed" && <span className="tm-map__note">{labels.error}</span>}
      {popupNode && popup ? createPortal(popup, popupNode) : null}
    </div>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any */
